import { openDB, type IDBPDatabase } from 'idb';
import type { Session } from '../../domain/puzzle/PuzzleSessionController';
import { validatePuzzle, type PuzzleDefinition } from '../../domain/puzzle/PuzzleDefinition';
import { localDay } from '../../domain/practice/calendar';
export type Confidence = 'whole-line' | 'idea' | 'lucky';
export interface Attempt {
  id: string;
  puzzleId: string;
  datasetVersion: string;
  mode: string;
  startedAt: number;
  completedAt: number;
  outcome: string;
  solvedFirstAttempt: boolean;
  wrongMoves: Session['mistakes'];
  hintsUsed: number;
  durationMs: number;
  themes: string[];
  rating: number;
  confidence?: Confidence;
  /** Review state before this attempt, so confidence edits can be reversed safely. */
  reviewBefore?: { step?: number; dueAt?: number };
  continuation?: Session['continuation'];
}
/** Ignore malformed history in summaries without removing it from storage or exports. */
export function isAttempt(value: unknown): value is Attempt {
  if (!value || typeof value !== 'object') return false;
  const a = value as Attempt;
  return (
    ['id', 'puzzleId', 'datasetVersion', 'mode'].every(
      (key) => typeof a[key as keyof Attempt] === 'string',
    ) &&
    Number.isFinite(a.startedAt) &&
    Number.isFinite(a.completedAt) &&
    ['solved', 'revealed', 'abandoned', 'skipped'].includes(a.outcome) &&
    typeof a.solvedFirstAttempt === 'boolean' &&
    Array.isArray(a.wrongMoves) &&
    Number.isSafeInteger(a.hintsUsed) &&
    a.hintsUsed >= 0 &&
    Number.isFinite(a.durationMs) &&
    Array.isArray(a.themes) &&
    Number.isSafeInteger(a.rating) &&
    a.rating >= 0 &&
    a.rating <= 4000
  );
}
export interface Aggregate {
  puzzleId: string;
  seenCount: number;
  solveCount: number;
  failureCount: number;
  lastSeenAt: number;
  /** Break ties when two attempts finish within the same clock tick. */
  lastAttemptId?: string;
  lastOutcome: string;
  reviewDueAt?: number;
  lastConfidence?: Confidence;
  /** R5: clean review solves in a row (1 → back in 7 days, 2 → 21 days, 3 → cleared). */
  reviewStep?: number;
}
/** R5: review intervals after a clean review solve, by step. Past the end, the puzzle is cleared. */
export const reviewIntervalsDays = [7, 21] as const;
/** R5: how many missed-puzzle positions are kept on the device for offline review. */
const missedCacheLimit = 200;
const stores = [
  'attempts',
  'puzzleAggregates',
  'favorites',
  'seenPuzzles',
  'offlineQueue',
  'sourceGames',
  'badPuzzleRecords',
  'dailyActivity',
] as const;
type StoreName = (typeof stores)[number];
interface StoreAccess {
  get<T>(store: StoreName, key: string): Promise<T | undefined>;
  all<T>(store: StoreName): Promise<T[]>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
  clear(store: StoreName): Promise<void>;
}
export class PersonalStore {
  private db?: IDBPDatabase;
  private memory = new Map<StoreName, Map<string, unknown>>(stores.map((s) => [s, new Map()]));
  private ready: Promise<void>;
  private writes = Promise.resolve();
  degraded = false;
  constructor(private notify: (message: string) => void = () => {}) {
    this.ready = Promise.resolve()
      .then(() =>
        openDB('aftermove', 1, {
          upgrade(db) {
            for (const s of stores) db.createObjectStore(s);
          },
        }),
      )
      .then((db) => {
        this.db = db;
      })
      .catch(() => {
        this.degraded = true;
        notify('Private storage is unavailable. Progress lasts for this session.');
      });
  }
  private async perform<T>(fn: () => Promise<T>, fallback: () => T): Promise<T> {
    await this.ready;
    const db = this.db;
    if (db)
      try {
        return await fn();
      } catch {
        // Concurrent failed requests may belong to the same connection. Close and
        // report it once; later failures still fall back to session memory.
        if (this.db === db) {
          db.close();
          this.db = undefined;
          this.degraded = true;
          this.notify('Storage failed. New progress is kept in memory for this session.');
        }
      }
    return fallback();
  }
  /** Keep read/modify/write operations indivisible, including across tabs. */
  private async atomic<T>(
    names: readonly StoreName[],
    operation: (data: StoreAccess) => Promise<T>,
  ) {
    await this.ready;
    const db = this.db;
    if (db) {
      let done: Promise<unknown> | undefined;
      let abort: (() => void) | undefined;
      const commits: (() => void)[] = [];
      const touched = new Set<string>();
      const cleared = new Set<StoreName>();
      try {
        const tx = db.transaction([...names], 'readwrite');
        done = tx.done;
        abort = () => tx.abort();
        // A synchronous quota/clone failure can abort before we reach tx.done.
        void done.catch(() => {});
        const result = await operation({
          get: async <V>(store: StoreName, key: string) => {
            const value = await tx.objectStore(store).get(key);
            if (!cleared.has(store) && !touched.has(`${store}:${key}`)) {
              if (value === undefined) this.memory.get(store)!.delete(key);
              else this.memory.get(store)!.set(key, structuredClone(value));
            }
            return value as V | undefined;
          },
          all: async <V>(store: StoreName) => {
            const [values, keys] = await Promise.all([
              tx.objectStore(store).getAll(),
              tx.objectStore(store).getAllKeys(),
            ]);
            if (!cleared.has(store) && ![...touched].some((key) => key.startsWith(`${store}:`)))
              this.memory.set(
                store,
                new Map(keys.map((key, i) => [String(key), structuredClone(values[i])])),
              );
            return values as V[];
          },
          put: async (store, key, value) => {
            await tx.objectStore(store).put(value, key);
            touched.add(`${store}:${key}`);
            const copy = structuredClone(value);
            commits.push(() => this.memory.get(store)!.set(key, copy));
          },
          remove: async (store, key) => {
            await tx.objectStore(store).delete(key);
            touched.add(`${store}:${key}`);
            commits.push(() => this.memory.get(store)!.delete(key));
          },
          clear: async (store) => {
            await tx.objectStore(store).clear();
            cleared.add(store);
            commits.push(() => this.memory.get(store)!.clear());
          },
        });
        await tx.done;
        for (const commit of commits) commit();
        return result;
      } catch {
        try {
          abort?.();
        } catch {
          /* Already aborted or completed. */
        }
        await done?.catch(() => {});
        if (this.db === db) {
          db.close();
          this.db = undefined;
          this.degraded = true;
          this.notify('Storage failed. New progress is kept in memory for this session.');
        }
      }
    }
    // Stage memory writes too: an operation that throws must not leave a partial save.
    const view = new Map(names.map((name) => [name, new Map(this.memory.get(name)!)]));
    const result = await operation({
      get: async <V>(store: StoreName, key: string) =>
        structuredClone(view.get(store)!.get(key)) as V | undefined,
      all: async <V>(store: StoreName) => structuredClone([...view.get(store)!.values()]) as V[],
      put: async (store, key, value) => {
        view.get(store)!.set(key, structuredClone(value));
      },
      remove: async (store, key) => {
        view.get(store)!.delete(key);
      },
      clear: async (store) => {
        view.get(store)!.clear();
      },
    });
    for (const [name, values] of view) this.memory.set(name, values);
    return result;
  }
  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const next = this.writes.then(operation);
    // One bad operation must not prevent later saves, export, or reset.
    this.writes = next.then(
      () => {},
      () => {},
    );
    return next;
  }
  async get<T>(store: StoreName, key: string): Promise<T | undefined> {
    await this.writes;
    return this.perform(
      async () => {
        const value = await this.db!.get(store, key);
        if (value === undefined) this.memory.get(store)!.delete(key);
        else this.memory.get(store)!.set(key, structuredClone(value));
        return value as T | undefined;
      },
      () => structuredClone(this.memory.get(store)!.get(key)) as T | undefined,
    );
  }
  async all<T>(store: StoreName): Promise<T[]> {
    await this.writes;
    return this.perform(
      async () => {
        const tx = this.db!.transaction(store, 'readonly');
        void tx.done.catch(() => {});
        const [values, keys] = await Promise.all([
          tx.store.getAll(),
          tx.store.getAllKeys(),
          tx.done,
        ]);
        this.memory.set(
          store,
          new Map(keys.map((key, i) => [String(key), structuredClone(values[i])])),
        );
        return values as T[];
      },
      () => structuredClone([...this.memory.get(store)!.values()]) as T[],
    );
  }
  async put(store: StoreName, key: string, value: unknown) {
    const copy = structuredClone(value);
    return this.enqueue(() => this.atomic([store], (data) => data.put(store, key, copy)));
  }
  async remove(store: StoreName, key: string) {
    return this.enqueue(() => this.atomic([store], (data) => data.remove(store, key)));
  }
  async seen() {
    // IndexedDB getAll() is key-ordered; API exclusions need chronological order.
    return new Set(
      (await this.all<{ id: string; at: number }>('seenPuzzles'))
        .filter(
          (record) =>
            record &&
            typeof record.id === 'string' &&
            /^[A-Za-z0-9_-]{1,64}$/.test(record.id) &&
            Number.isFinite(record.at),
        )
        .sort((a, b) => a.at - b.at)
        .map((r) => r.id),
    );
  }
  async lastShown() {
    return this.get<string>('offlineQueue', 'lastShown');
  }
  async markSeen(p: PuzzleDefinition) {
    const record = { id: p.id, at: Date.now() };
    return this.enqueue(() =>
      this.atomic(['seenPuzzles', 'offlineQueue'], async (data) => {
        await data.put('seenPuzzles', p.id, record);
        await data.put('offlineQueue', 'lastShown', p.id);
        const prefetch = await data.get<PuzzleDefinition[]>('offlineQueue', 'prefetch');
        if (Array.isArray(prefetch))
          await data.put(
            'offlineQueue',
            'prefetch',
            prefetch.filter((q) => q && typeof q.id === 'string' && q.id !== p.id),
          );
      }),
    );
  }
  async cache(puzzles: PuzzleDefinition[]) {
    const copy = structuredClone(puzzles);
    return this.enqueue(() =>
      this.atomic(['seenPuzzles', 'offlineQueue'], async (data) => {
        const seen = new Set(
          (await data.all<{ id: string }>('seenPuzzles'))
            .filter((record) => record && typeof record.id === 'string')
            .map((record) => record.id),
        );
        await data.put(
          'offlineQueue',
          'prefetch',
          copy.filter((p) => !seen.has(p.id)).slice(0, 24),
        );
      }),
    );
  }
  async cached() {
    return (await this.get<PuzzleDefinition[]>('offlineQueue', 'prefetch')) ?? [];
  }
  /** R5: a puzzle kept on this device (missed for review, or a favourite), for offline loading. */
  async savedPuzzle(id: string) {
    const validCopy = (candidate: unknown) => {
      try {
        const valid = validatePuzzle(candidate);
        if (valid.id === id) return valid;
      } catch {
        /* One corrupt copy must not hide another usable offline copy. */
      }
    };
    const missed = validCopy(await this.get('offlineQueue', `missed:${id}`));
    if (missed) return missed;
    return validCopy((await this.get<{ puzzle: PuzzleDefinition }>('favorites', id))?.puzzle);
  }
  async favorite(p: PuzzleDefinition) {
    const copy = structuredClone(p);
    return this.enqueue(() =>
      this.atomic(['favorites'], async (data) => {
        if (await data.get('favorites', copy.id)) {
          await data.remove('favorites', copy.id);
          return false;
        }
        await data.put('favorites', copy.id, { id: copy.id, at: Date.now(), puzzle: copy });
        return true;
      }),
    );
  }
  save(session: Session, mode: string, confidence?: Confidence): Promise<void> {
    const copy = structuredClone(session);
    return this.enqueue(() =>
      this.atomic(
        ['attempts', 'puzzleAggregates', 'offlineQueue', 'dailyActivity'],
        async (data) => {
          let requestPersistence = false;
          const completedAt = copy.completedAt ?? Date.now();
          // A stable attempt key excludes generation changes made by engine cancellation.
          const attemptId = `${copy.puzzle.id}:${copy.attemptId ?? copy.startedAt}`;
          const old = await data.get<Attempt>('attempts', attemptId);
          const p = copy.puzzle;
          const previous = await data.get<Aggregate>('puzzleAggregates', p.id);
          const reviewBefore = old?.reviewBefore ?? {
            step: Math.max(
              0,
              (previous?.reviewStep ?? 0) -
                (old?.mode === 'review' && old.solvedFirstAttempt && old.confidence !== 'lucky'
                  ? 1
                  : 0),
            ),
            dueAt: previous?.reviewDueAt,
          };
          const attempt: Attempt = {
            id: attemptId,
            puzzleId: copy.puzzle.id,
            datasetVersion: copy.puzzle.datasetVersion,
            mode,
            startedAt: copy.startedAt,
            completedAt,
            outcome: copy.outcome ?? 'abandoned',
            solvedFirstAttempt:
              copy.solved && !copy.mistakes.length && !copy.hintsUsed && !copy.revealed,
            wrongMoves: copy.mistakes,
            hintsUsed: copy.hintsUsed,
            durationMs: Math.max(0, completedAt - copy.startedAt),
            themes: copy.puzzle.themes,
            rating: copy.puzzle.rating,
            confidence: confidence ?? old?.confidence,
            reviewBefore,
            continuation: copy.continuation,
          };
          await data.put('attempts', attemptId, attempt);
          const missed =
            !!copy.mistakes.length ||
            !!copy.hintsUsed ||
            !copy.solved ||
            attempt.confidence === 'lucky';
          const reviewDays =
            attempt.confidence === 'lucky' ? 2 : copy.solved && copy.mistakes.length ? 3 : 1;
          const previouslySolved = old && ['solved', 'revealed'].includes(old.outcome) ? 1 : 0;
          const previouslyMissed =
            old &&
            (old.wrongMoves.length ||
              old.hintsUsed ||
              !previouslySolved ||
              old.confidence === 'lucky')
              ? 1
              : 0;
          // R5: clean review solves step the puzzle out of review; any miss starts over.
          const cleanReview = !missed && mode === 'review' && copy.solved;
          const reviewStep = missed
            ? 0
            : cleanReview
              ? (reviewBefore.step ?? 0) + 1
              : reviewBefore.step;
          const reviewInterval = cleanReview
            ? reviewIntervalsDays[(reviewStep ?? 1) - 1]
            : undefined;
          const aggregate: Aggregate = {
            puzzleId: p.id,
            seenCount: (previous?.seenCount ?? 0) + (old ? 0 : 1),
            solveCount: (previous?.solveCount ?? 0) + Number(copy.solved) - previouslySolved,
            failureCount: (previous?.failureCount ?? 0) + Number(missed) - previouslyMissed,
            lastSeenAt: completedAt,
            lastAttemptId: attemptId,
            lastOutcome: attempt.outcome,
            lastConfidence: attempt.confidence,
            reviewDueAt: missed
              ? completedAt + reviewDays * 86400000
              : reviewInterval
                ? completedAt + reviewInterval * 86400000
                : cleanReview
                  ? undefined
                  : reviewBefore.dueAt,
            ...(reviewStep !== undefined ? { reviewStep } : {}),
          };
          // Updating an older attempt must not undo a later tab's review progress.
          const latest =
            !previous ||
            completedAt > previous.lastSeenAt ||
            (completedAt === previous.lastSeenAt &&
              (!old || !previous.lastAttemptId || previous.lastAttemptId === attemptId));
          if (!latest) {
            aggregate.lastSeenAt = previous.lastSeenAt;
            aggregate.lastAttemptId = previous.lastAttemptId;
            aggregate.lastOutcome = previous.lastOutcome;
            aggregate.lastConfidence = previous.lastConfidence;
            aggregate.reviewStep = previous.reviewStep;
            aggregate.reviewDueAt = previous.reviewDueAt;
          }
          await data.put('puzzleAggregates', p.id, aggregate);
          // Keep missed positions on the device so review works offline.
          if (latest && aggregate.reviewDueAt !== undefined) await this.keepMissed(p, data);
          else if (latest) {
            await data.remove('offlineQueue', `missed:${p.id}`);
            const index = await data.get<unknown>('offlineQueue', 'missed');
            if (Array.isArray(index))
              await data.put(
                'offlineQueue',
                'missed',
                index.filter((id) => id !== p.id),
              );
          }
          if (copy.solved) {
            const day = localDay(completedAt);
            const activity = await data.get<{ solved: number }>('dailyActivity', day);
            await data.put('dailyActivity', day, {
              day,
              solved: (activity?.solved ?? 0) + 1 - previouslySolved,
            });
            if (!(await data.get('offlineQueue', 'storage-persist-requested'))) {
              await data.put('offlineQueue', 'storage-persist-requested', true);
              requestPersistence = true;
            }
          }
          return requestPersistence;
        },
      ),
    ).then((requestPersistence) => {
      // Run browser-side effects only after a successful atomic save.
      if (requestPersistence) {
        try {
          void globalThis.navigator?.storage?.persist?.().catch(() => {});
        } catch {
          /* Best effort. */
        }
      }
    });
  }
  private async keepMissed(p: PuzzleDefinition, data: StoreAccess) {
    await data.put('offlineQueue', `missed:${p.id}`, p);
    const saved = await data.get<unknown>('offlineQueue', 'missed');
    const index = (Array.isArray(saved) ? saved : []).filter(
      (id): id is string => typeof id === 'string' && id !== p.id,
    );
    index.push(p.id);
    for (const dropped of index.splice(0, Math.max(0, index.length - missedCacheLimit)))
      await data.remove('offlineQueue', `missed:${dropped}`);
    await data.put('offlineQueue', 'missed', index);
  }
  async export() {
    return this.enqueue(() =>
      this.atomic(stores, async (data) => ({
        schemaVersion: 1,
        exportedAt: new Date().toISOString(),
        ...Object.fromEntries(await Promise.all(stores.map(async (s) => [s, await data.all(s)]))),
      })),
    );
  }
  async reset() {
    return this.enqueue(() =>
      this.atomic(stores, async (data) => {
        for (const s of stores) await data.clear(s);
      }),
    );
  }
}
